import { isEmail } from '@atink/domain';

/**
 * D05-11 (Anas, 6 Oct): an email is mandatory when a deposit is TAKEN. Every
 * staff take-money form (card over the phone, EFTPOS / cash at the desk, take
 * deposit on a held booking) shows this field: the email already on file (read
 * only — a correction goes through Edit details, with its history line) or a
 * required input when there is none. Sending a link needs no email up front:
 * the customer's pay page collects it.
 */

interface Customerish {
  email?: string | null;
}

/** The customer's usable email on file, or ''. */
export function emailOnFile(customer: Customerish | undefined): string {
  const e = (customer?.email ?? '').trim();
  return isEmail(e) ? e : '';
}

/** The email a staff payment sends: the one on file, else the typed one when valid. */
export function depositEmailFor(customer: Customerish | undefined, typed: string): string | undefined {
  const onFile = emailOnFile(customer);
  if (onFile) return onFile;
  return isEmail(typed) ? typed.trim().toLowerCase() : undefined;
}

export default function DepositEmailField({
  customer,
  value,
  onChange,
  className,
}: {
  customer: Customerish | undefined;
  value: string;
  onChange: (v: string) => void;
  className?: string;
}) {
  const onFile = emailOnFile(customer);
  if (onFile) {
    return (
      <p className={`font-inter text-xs text-cream-soft ${className ?? ''}`} data-testid="deposit-email-onfile">
        Email on file · <span className="text-cream">{onFile}</span>
        <span className="text-cream-faint"> (change it with Edit details)</span>
      </p>
    );
  }
  const invalid = value.trim().length > 0 && !isEmail(value);
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="label-small-caps mb-1 block">Client&apos;s email · needed to take a deposit</span>
      <input
        type="email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="name@example.com"
        data-testid="deposit-take-email"
        aria-invalid={invalid || undefined}
        className="w-full rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream placeholder:text-cream-faint focus:border-gold-bright focus:outline-none"
      />
      {invalid ? (
        <span className="mt-1 block font-inter text-[11px] text-orange" data-testid="deposit-take-email-invalid">
          That doesn&apos;t look like an email address.
        </span>
      ) : null}
    </label>
  );
}
